import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Check } from 'lucide-react';

const BusinessCardPage = () => {
  const frontCardRef = useRef(null);
  const backCardRef = useRef(null);

  // Card dimensions: 85mm x 55mm at 300 DPI = 1004px x 650px
  // For screen display, we'll use a scaled version

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-20" data-testid="business-card-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="section-container">
          <nav className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/mushonga_logo_circular.png"
                alt="Mushonga Systems"
                className="h-10 w-auto"
              />
              <span className="text-white font-bold text-lg hidden sm:block">
                Mushonga Systems
              </span>
            </Link>
            <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">Back to Home</span>
            </Link>
          </nav>
        </div>
      </header>

      <div className="section-container pt-16">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
            Brand Assets
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-white mb-4">
            Business Card Design
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Premium double-sided business card design for Mushonga Systems. UK Standard size: 85mm × 55mm.
          </p>
        </div>

        {/* Cards Display */}
        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          
          {/* Front Card */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-center">Front</h3>
            <div className="flex justify-center">
              <div 
                ref={frontCardRef}
                className="relative overflow-hidden shadow-2xl"
                style={{
                  width: '425px',
                  height: '275px',
                  borderRadius: '0px',
                  background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                }}
              >
                {/* Subtle grid pattern */}
                <div 
                  className="absolute inset-0 opacity-[0.03]"
                  style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}
                />
                
                {/* Accent line at top */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500" />
                
                {/* Content */}
                <div className="relative h-full flex flex-col justify-between p-8">
                  {/* Top - Logo and Company */}
                  <div className="flex items-center gap-3">
                    <img 
                      src="/mushonga_logo_circular.png"
                      alt="Mushonga Systems"
                      className="w-10 h-10"
                    />
                    <div>
                      <h2 className="text-white font-bold text-lg tracking-tight" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                        Mushonga Systems
                      </h2>
                      <p className="text-gray-400 text-[10px] tracking-wider uppercase">
                        Modern Websites • Automation • CRM Systems
                      </p>
                    </div>
                  </div>
                  
                  {/* Middle - Name and Title */}
                  <div className="text-center">
                    <h1 className="text-white text-2xl font-bold tracking-tight mb-1" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                      Adam Mushonga
                    </h1>
                    <p className="text-emerald-400 text-sm font-medium">
                      Automation & Web Systems Specialist
                    </p>
                  </div>
                  
                  {/* Bottom - Contact Info */}
                  <div className="flex justify-center gap-8 text-xs">
                    <div className="text-center">
                      <p className="text-gray-500 uppercase tracking-wider mb-1" style={{ fontSize: '8px' }}>Phone</p>
                      <p className="text-white font-medium">07539 249987</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-500 uppercase tracking-wider mb-1" style={{ fontSize: '8px' }}>Email</p>
                      <p className="text-white font-medium">adam@mushongasystems.com</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-500 uppercase tracking-wider mb-1" style={{ fontSize: '8px' }}>Web</p>
                      <p className="text-white font-medium">mushongasystems.com</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Back Card */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-center">Back</h3>
            <div className="flex justify-center">
              <div 
                ref={backCardRef}
                className="relative overflow-hidden shadow-2xl"
                style={{
                  width: '425px',
                  height: '275px',
                  borderRadius: '0px',
                  background: '#0F172A',
                }}
              >
                {/* Green accent sidebar */}
                <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-b from-emerald-400 to-emerald-600" />
                
                {/* Subtle pattern */}
                <div 
                  className="absolute inset-0 opacity-[0.02]"
                  style={{
                    backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
                    backgroundSize: '24px 24px'
                  }}
                />
                
                {/* Content */}
                <div className="relative h-full flex p-6 pl-8">
                  {/* Left Content */}
                  <div className="flex-1 flex flex-col justify-between pr-6">
                    {/* Headline */}
                    <div>
                      <p className="text-white text-base font-semibold leading-tight mb-4" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                        "Helping businesses save time, reduce admin and win more customers."
                      </p>
                    </div>
                    
                    {/* Services */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                      {[
                        'Website Design',
                        'Business Automation',
                        'CRM Systems',
                        'Booking Platforms',
                        'AI Workflows',
                        'Custom Business Software'
                      ].map((service, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Check className="text-emerald-400 flex-shrink-0" size={12} />
                          <span className="text-gray-300 text-[11px]">{service}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Logo */}
                    <div className="flex items-center gap-2 mt-4">
                      <img 
                        src="/mushonga_logo_circular.png"
                        alt="Mushonga Systems"
                        className="w-6 h-6 opacity-60"
                      />
                      <span className="text-gray-500 text-[10px] font-medium">mushongasystems.com</span>
                    </div>
                  </div>
                  
                  {/* Right - QR Code */}
                  <div className="flex flex-col items-center justify-center pl-6 border-l border-white/10">
                    <div className="bg-white p-3 rounded-lg mb-3">
                      {/* QR Code placeholder - using a generated QR code pattern */}
                      <svg width="100" height="100" viewBox="0 0 100 100" className="block">
                        <rect width="100" height="100" fill="white"/>
                        {/* QR Code pattern for mushongasystems.com */}
                        <g fill="#0F172A">
                          {/* Position detection patterns */}
                          <rect x="4" y="4" width="24" height="24"/>
                          <rect x="8" y="8" width="16" height="16" fill="white"/>
                          <rect x="12" y="12" width="8" height="8"/>
                          
                          <rect x="72" y="4" width="24" height="24"/>
                          <rect x="76" y="8" width="16" height="16" fill="white"/>
                          <rect x="80" y="12" width="8" height="8"/>
                          
                          <rect x="4" y="72" width="24" height="24"/>
                          <rect x="8" y="76" width="16" height="16" fill="white"/>
                          <rect x="12" y="80" width="8" height="8"/>
                          
                          {/* Timing patterns */}
                          <rect x="32" y="8" width="4" height="4"/>
                          <rect x="40" y="8" width="4" height="4"/>
                          <rect x="48" y="8" width="4" height="4"/>
                          <rect x="56" y="8" width="4" height="4"/>
                          <rect x="64" y="8" width="4" height="4"/>
                          
                          <rect x="8" y="32" width="4" height="4"/>
                          <rect x="8" y="40" width="4" height="4"/>
                          <rect x="8" y="48" width="4" height="4"/>
                          <rect x="8" y="56" width="4" height="4"/>
                          <rect x="8" y="64" width="4" height="4"/>
                          
                          {/* Data modules - simplified pattern */}
                          <rect x="32" y="32" width="4" height="4"/>
                          <rect x="40" y="32" width="4" height="4"/>
                          <rect x="52" y="32" width="4" height="4"/>
                          <rect x="64" y="32" width="4" height="4"/>
                          
                          <rect x="36" y="40" width="4" height="4"/>
                          <rect x="48" y="40" width="4" height="4"/>
                          <rect x="56" y="40" width="4" height="4"/>
                          <rect x="72" y="40" width="4" height="4"/>
                          <rect x="84" y="40" width="4" height="4"/>
                          
                          <rect x="32" y="48" width="4" height="4"/>
                          <rect x="44" y="48" width="4" height="4"/>
                          <rect x="60" y="48" width="4" height="4"/>
                          <rect x="76" y="48" width="4" height="4"/>
                          <rect x="88" y="48" width="4" height="4"/>
                          
                          <rect x="36" y="56" width="4" height="4"/>
                          <rect x="52" y="56" width="4" height="4"/>
                          <rect x="64" y="56" width="4" height="4"/>
                          <rect x="80" y="56" width="4" height="4"/>
                          
                          <rect x="32" y="64" width="4" height="4"/>
                          <rect x="40" y="64" width="4" height="4"/>
                          <rect x="56" y="64" width="4" height="4"/>
                          <rect x="72" y="64" width="4" height="4"/>
                          <rect x="88" y="64" width="4" height="4"/>
                          
                          <rect x="36" y="72" width="4" height="4"/>
                          <rect x="48" y="72" width="4" height="4"/>
                          <rect x="60" y="72" width="4" height="4"/>
                          <rect x="76" y="72" width="4" height="4"/>
                          
                          <rect x="32" y="80" width="4" height="4"/>
                          <rect x="44" y="80" width="4" height="4"/>
                          <rect x="52" y="80" width="4" height="4"/>
                          <rect x="64" y="80" width="4" height="4"/>
                          <rect x="80" y="80" width="4" height="4"/>
                          <rect x="88" y="80" width="4" height="4"/>
                          
                          <rect x="36" y="88" width="4" height="4"/>
                          <rect x="48" y="88" width="4" height="4"/>
                          <rect x="56" y="88" width="4" height="4"/>
                          <rect x="72" y="88" width="4" height="4"/>
                          <rect x="84" y="88" width="4" height="4"/>
                        </g>
                      </svg>
                    </div>
                    <p className="text-emerald-400 text-[10px] font-semibold uppercase tracking-wider text-center">
                      Scan for Free
                    </p>
                    <p className="text-white text-[11px] font-medium text-center">
                      Website & Systems Review
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mockups Section */}
        <div className="mt-20">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Card Mockups</h2>
          
          {/* Desk Mockup */}
          <div className="max-w-4xl mx-auto mb-12">
            <div 
              className="relative rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                padding: '60px'
              }}
            >
              {/* Shadow effect */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30" />
              
              {/* Cards on desk */}
              <div className="relative flex justify-center items-center gap-8">
                {/* Front card - angled */}
                <div 
                  className="relative shadow-2xl transform -rotate-6"
                  style={{
                    width: '340px',
                    height: '220px',
                    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                  }}
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500" />
                  <div className="h-full flex flex-col justify-between p-6">
                    <div className="flex items-center gap-2">
                      <img 
                        src="/mushonga_logo_circular.png"
                        alt="Logo"
                        className="w-8 h-8"
                      />
                      <div>
                        <p className="text-white font-bold text-sm">Mushonga Systems</p>
                        <p className="text-gray-400 text-[8px]">Modern Websites • Automation • CRM Systems</p>
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-white text-lg font-bold">Adam Mushonga</p>
                      <p className="text-emerald-400 text-xs">Automation & Web Systems Specialist</p>
                    </div>
                    <div className="flex justify-center gap-6 text-[9px]">
                      <span className="text-white">07539 249987</span>
                      <span className="text-white">adam@mushongasystems.com</span>
                    </div>
                  </div>
                </div>
                
                {/* Back card - angled opposite */}
                <div 
                  className="relative shadow-2xl transform rotate-6"
                  style={{
                    width: '340px',
                    height: '220px',
                    background: '#0F172A',
                  }}
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-emerald-400 to-emerald-600" />
                  <div className="h-full flex p-5 pl-6">
                    <div className="flex-1">
                      <p className="text-white text-xs font-semibold mb-3 leading-tight">
                        "Helping businesses save time, reduce admin and win more customers."
                      </p>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                        {['Website Design', 'Business Automation', 'CRM Systems', 'Booking Platforms', 'AI Workflows', 'Custom Software'].map((s, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <Check className="text-emerald-400" size={8} />
                            <span className="text-gray-300 text-[8px]">{s}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center pl-4 border-l border-white/10">
                      <div className="bg-white p-2 rounded mb-2">
                        <div className="w-16 h-16 bg-gray-300 rounded-sm"></div>
                      </div>
                      <p className="text-emerald-400 text-[7px] font-semibold">SCAN FOR FREE</p>
                      <p className="text-white text-[8px]">Systems Review</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Reflection */}
              <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white/5 to-transparent" />
            </div>
            <p className="text-gray-500 text-sm text-center mt-4">Desk Mockup</p>
          </div>
        </div>

        {/* Print Specifications */}
        <div className="mt-16 max-w-2xl mx-auto">
          <h2 className="text-xl font-bold text-white text-center mb-6">Print Specifications</h2>
          <div className="bg-[#111827] border border-white/10 rounded-xl p-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {[
                { label: 'Size', value: '85mm × 55mm (UK Standard)' },
                { label: 'Resolution', value: '300 DPI' },
                { label: 'Color Mode', value: 'CMYK Ready' },
                { label: 'Bleed', value: '3mm included' },
                { label: 'Finish', value: 'Matte' },
                { label: 'Corners', value: 'Square' },
              ].map((spec, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-gray-500">{spec.label}</span>
                  <span className="text-white font-medium">{spec.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-12 max-w-2xl mx-auto text-center">
          <p className="text-gray-400 text-sm">
            To export these designs for print, take high-resolution screenshots or use the browser's print function.
            For professional printing, the designs should be recreated in Adobe Illustrator or Figma at 300 DPI.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BusinessCardPage;
